import React, { memo } from 'react';

import { MyBadge } from 'components/FileManager/ItemsViewer/Survey/SurveyResponseViewer/styles';
import { stringToColour } from '../../utils/style/colors';
import { isANumber } from '../../utils/number';
import { ITags } from 'store/providers/providerTypes';
import {
  IAnswersView,
  IChoiceView,
  IMultipleChoiceAnswerView,
  IMultipleChoiceQuestionView,
  ISurveyProperties,
  ISurveyView,
} from 'store/survey/surveyTypes';

interface IComponentProps {
  tags: ITags['data'];
  version?: ISurveyView;
  answers: Array<IAnswersView>;
  questions: IMultipleChoiceQuestionView[];
}

export function getDistinctiveAnswerAsString(
  tags: ITags['data'],
  answers: IAnswersView[],
  questions: IMultipleChoiceQuestionView[],
  version?: ISurveyProperties
) {
  let ret = '';
  questions.forEach((q) => {
    const answer = answers.find(
      (ans) => !!ans && ans.questionId === q.id
    ) as IMultipleChoiceAnswerView;
    if (answer) {
      let values = [];
      const valueScoreMap: { [key: string]: number | null } = {};
      let maxScore = q.choiceList.reduce(
        (current: number | null, item: IChoiceView) => {
          const score = item.score;
          if (isANumber(score)) {
            if (current === null) return score;
            else return Math.max(Number(score), current);
          }
          return current;
        },
        null
      );
      if (maxScore === null && version?.stageQuestion === q.id) {
        maxScore = q.choiceList.length - 1;
      }
      if (q.useTags) {
        values = answer.selected.map((tagId: number) => {
          const tagTitle = tags[tagId]?.text || tags[tagId.toString()]?.text;
          if (version?.stageQuestion === q.id) {
            maxScore = (q.tagChoices?.length || 0) - 1;
            valueScoreMap[tagTitle] = (q.tagChoices || [])
              ?.sort((a, b) => (tags[a]?.order || 0) - (tags[b]?.order || 0))
              .indexOf(tagId);
          }
          return tagTitle;
        });
      } else {
        values = (answer.values || [answer.value]).map((val: string) => {
          const choiceIndex = q.choiceList.findIndex((ch) => ch.value === val);
          const choice =
            choiceIndex >= 0 ? q.choiceList[choiceIndex] : undefined;
          if (choice)
            valueScoreMap[choice.text] =
              choice.score ??
              (version?.stageQuestion === q.id ? choiceIndex : null);
          return choice ? choice.text : null;
        });
      }
      values = values.filter((val: any) => !!val);
      return values.forEach((val) => {
        ret += (ret.length > 0 ? ', ' : '') + val;
      });
    }
  });
  return ret;
}

const DistinctiveAnswers: React.FC<IComponentProps> = ({
  tags,
  answers,
  questions,
  version,
}) => (
  <React.Fragment>
    {questions.map((q) => {
      const answer = answers.find((ans) => !!ans && ans.questionId === q.id);
      if (answer) {
        let values = [];
        const valueScoreMap: { [key: string]: number | null } = {};
        let maxScore = q.choiceList.reduce(
          (current: number | null, item: IChoiceView) => {
            const score = item.score;
            if (isANumber(score)) {
              if (current === null) return score;
              else return Math.max(Number(score), current);
            }
            return current;
          },
          null
        );
        if (maxScore === null && version?.stageQuestion === q.id) {
          maxScore = q.choiceList.length - 1;
        }
        if (q.useTags) {
          // @ts-expect-error it's ok
          values = answer.selected.map((tagId: number) => {
            const tagTitle = tags[tagId]?.text || tags[tagId.toString()]?.text;
            if (version?.stageQuestion === q.id) {
              maxScore = (q.tagChoices?.length || 0) - 1;
              valueScoreMap[tagTitle] = [...(q.tagChoices || [])]
                ?.sort((a, b) => {
                  if (isANumber(tags[a]?.order) && isANumber(tags[b]?.order))
                    return tags[a].order! - tags[b].order!;

                  return 0;
                })
                .indexOf(tagId);
            }
            return tagTitle;
          });
        } else {
          // @ts-expect-error it's an array
          values = (answer.values || [answer.value]).map((val: string) => {
            const choiceIndex = q.choiceList.findIndex(
              (ch) => ch.value === val
            );
            const choice =
              choiceIndex >= 0 ? q.choiceList[choiceIndex] : undefined;
            if (choice)
              valueScoreMap[choice.text] =
                choice.score ??
                (version?.stageQuestion === q.id ? choiceIndex : null);
            return choice ? choice.text : null;
          });
        }
        values = values.filter((val: any) => !!val);
        return values.map((val: string) => (
          <MyBadge
            key={val}
            color={stringToColour(
              q.title,
              1,
              false,
              val,
              valueScoreMap[val],
              maxScore
            )}
          >
            {val}
          </MyBadge>
        ));
      }
      return null;
    })}
  </React.Fragment>
);

export default memo(DistinctiveAnswers);
